/* ============================================================
   PES Brand Rail hydration (2026-09-25, Fix C)
   ------------------------------------------------------------
   The server-rendered front-runners rail (snippets/pes-featured-rail)
   prefers the collection's own brand, but Liquid can only see the
   collection's first 50 products — on best-selling sorts dominated
   by sold-out PES System Kits, fewer than 6 available own-brand
   products are visible and the rail falls back to the curated
   cross-brand list (the bug: Trina/ZnShine/Longi on the Canadian
   Solar page).

   This script completes the job client-side: when the server rail
   reports data-brandcount < 6, it fetches /collections/<handle>/
   products.json?limit=250, keeps available products whose vendor
   name appears in the collection title (pseudo-vendors excluded),
   and — when at least 6 exist — rebuilds the rail track with
   same-design brand cards. Any failure leaves the server rail
   untouched.
   ============================================================ */
(function () {
  'use strict';

  var JUNK = { 'pes supply': 1, 'pes global': 1, 'pes global group': 1, pes: 1, 'pes system kits': 1, 'pes system kit': 1, outbound: 1, 'outbound brands': 1, 'enf solar': 1, 'portlandia electric supply': 1, 'portlandia electric': 1 };

  function money(cents) {
    return '$' + (cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }
  function esc(s) {
    return String(s == null ? '' : s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }
  function trunc(s, n) {
    s = String(s || '');
    return s.length > n ? s.slice(0, n - 1) + '\u2026' : s;
  }
  function wattOf(title) {
    var m = String(title || '').toLowerCase().replace(/[\(\),]/g, ' ').match(/\b(\d{3})\s*w(?:att)?s?\b/);
    if (m) { var w = parseInt(m[1], 10); if (w >= 100 && w <= 800) return w; }
    return 0;
  }

  /* PES 2026-08-21 facelift: $/W chip is panels-only. Positive panel signal
     (product_type or title) or a bare 100-800W token with no exclusion word. */
  function isPanel(p, w) {
    var t = String(p.product_type || '').toLowerCase();
    var tl = String(p.title || '').toLowerCase();
    if (/(inverter|generator|battery|kit|bundle|pallet)/.test(tl)) return false;
    if (t.indexOf('panel') >= 0 || t.indexOf('module') >= 0) return true;
    if (/solar panel| module/.test(tl)) return true;
    return w > 0;
  }

  /* PES 2026-08-21 facelift: badge row — stock truth + ship line, fetched from
     each product's .js endpoint (products.json exposes no inventory data).
     Failure leaves the card without a stock line (never fabricated). */
  function annotateStock(track, rendered) {
    var els = track.querySelectorAll('.pes-fr-card');
    Promise.all(rendered.map(function (r) {
      return fetch('/products/' + encodeURIComponent(r.p.handle) + '.js')
        .then(function (x) { return x.ok ? x.json() : null; })
        .catch(function () { return null; });
    })).then(function (infos) {
      infos.forEach(function (info, i) {
        var el = els[i];
        if (!el || !info) return;
        var v = null;
        (info.variants || []).forEach(function (x) { if (!v && x.available) v = x; });
        if (!v) v = (info.variants || [])[0];
        if (!v) return;
        var qty = v.inventory_quantity || 0;
        var state = 'order';
        if (v.inventory_management === 'shopify') {
          if (qty > 5) state = 'in';
          else if (qty >= 1) state = 'low';
        }
        var label = state === 'in'
          ? (qty <= 10 ? 'In stock \u2014 ' + qty + ' left' : 'In stock')
          : state === 'low' ? 'Only ' + qty + ' left' : 'Available to order';
        var s = document.createElement('span');
        s.className = 'pes-fr-card__stock pes-fr-card__stock--' + state;
        s.textContent = label;
        el.appendChild(s);
        var sh = document.createElement('span');
        sh.className = 'pes-fr-card__ship';
        sh.textContent = (state === 'in' || state === 'low') ? 'Ships in 1\u20132 business days' : 'Ships in 7\u201310 business days';
        el.appendChild(sh);
      });
    });
  }

  function cardHtml(p, idx, brand) {
    var price = null, compare = null, avail = false;
    (p.variants || []).forEach(function (v) {
      var pr = Math.round(parseFloat(v.price) * 100);
      if (!isNaN(pr) && (price === null || pr < price)) price = pr;
      var cp = v.compare_at_price ? Math.round(parseFloat(v.compare_at_price) * 100) : null;
      if (cp && (compare === null || cp > compare)) compare = cp;
      if (v.available) avail = true;
    });
    if (!avail || price === null) return '';
    var img = (p.images && p.images[0] && p.images[0].src) || '';
    if (img) img = img.replace(/\.(png|jpg|jpeg|webp)(\?|$)/i, '_300x.$1$2');
    var w = wattOf(p.title);
    var h = '<a class="pes-fr-card" href="/products/' + esc(p.handle) + '">';
    if (idx < 3) h += '<span class="pes-fr-card__badge">Top Seller</span>';
    if (img) h += '<img class="pes-fr-card__img" src="' + esc(img) + '" alt="' + esc(p.title) + '" width="150" height="150" loading="lazy">';
    h += '<span class="pes-fr-card__vendor">' + esc(brand) + '</span>';
    h += '<span class="pes-fr-card__title">' + esc(trunc(p.title, 58)) + '</span>';
    h += '<span class="pes-fr-card__price">' + money(price);
    if (compare && compare > price) h += '<s>' + money(compare) + '</s>';
    h += '</span>';
    if (w > 0 && isPanel(p, w)) {
      var pw = Math.round(price / w); /* cents per watt */
      var pwDollars = pw / 100;
      if (pwDollars >= 0.15 && pwDollars <= 3.0) { /* same sanity band as card-product */
        h += '<span class="pes-fr-card__wpd">$' + pwDollars.toFixed(2) + '/W \u00b7 ' + w + 'W</span>';
      }
    }
    h += '</a>';
    return h;
  }

  function init() {
    var rail = document.querySelector('[data-pes-rail]');
    if (!rail) return;
    if (rail.getAttribute('data-pes-rail-hydrated') === '1') return;
    var bc = parseInt(rail.getAttribute('data-brandcount') || '0', 10);
    if (bc >= 6) return; /* server already rendered the brand rail */
    var handle = rail.getAttribute('data-handle') || '';
    if (!handle) return;
    var m = window.location.pathname.match(/\/collections\/([^\/\?]+)/);
    if (!m || m[1] !== handle) return;

    /* Collection title from the page H1 (fallback: handleized guess) */
    var titleEl = document.querySelector('h1');
    var collTitle = (titleEl ? titleEl.textContent : handle.replace(/-/g, ' ')).toLowerCase();

    rail.setAttribute('data-pes-rail-hydrated', '1');
    fetch('/collections/' + handle + '/products.json?limit=250')
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (d) {
        if (!d || !d.products) return;
        /* pick the brand: vendor (len>=4, not junk) contained in the collection title */
        var byVendor = {};
        d.products.forEach(function (p) {
          var v = String(p.vendor || '').trim();
          var vl = v.toLowerCase().replace(/\s+/g, ' ');
          if (vl.length < 4 || JUNK[vl]) return;
          if (collTitle.indexOf(vl) < 0) return;
          var avail = (p.variants || []).some(function (x) { return x.available; });
          if (!avail) return;
          (byVendor[v] = byVendor[v] || []).push(p);
        });
        var best = null;
        Object.keys(byVendor).forEach(function (v) {
          if (byVendor[v].length >= 6 && (!best || byVendor[v].length > byVendor[best].length)) best = v;
        });
        if (!best) return; /* truly fewer than 6 -> keep cross-brand fallback */
        var rendered = [];
        byVendor[best].slice(0, 10).forEach(function (p, i) {
          var hh = cardHtml(p, i, best);
          if (hh) rendered.push({ p: p, h: hh });
        });
        var cards = rendered.map(function (r) { return r.h; }).join('');
        if (!cards) return;
        var track = rail.querySelector('.pes-fr-rail__track');
        if (track) {
          track.innerHTML = cards;
          annotateStock(track, rendered);
        }
        if (window.console && console.debug) console.debug('[pes-brand-rail] hydrated', byVendor[best].length, best, 'products');
      })
      .catch(function () { /* keep server rail */ });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  document.addEventListener('shopify:section:load', function () { setTimeout(init, 60); });
})();
